import { type ComponentPropsWithoutRef } from "react"

import {
  getCustomerCompanyRetrieveQueryKey,
  useCustomerRepositoryArticlesOfIncorporationRetrieve,
  useCustomerRepositoryArticlesOfIncorporationUpdate,
} from "@boundless/bl-api-client/customer/services"
import { CustomerRepositoryCreateBody as FormSchema } from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { ButtonBack } from "@/components/buttons/ButtonBack"
import { ButtonNext } from "@/components/buttons/ButtonNext"
import { FileUpload } from "@/components/form-elements/FileUploadGroup"
import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type { RepositoryDocument } from "@boundless/bl-api-client/customer/models"

interface ArticlesOfIncorporationFormProps extends ComponentPropsWithoutRef<"form"> {
  companyUuid: string
  initialArticlesOfIncorporation?: RepositoryDocument
  onNext: () => void
  onBack?: () => void
  onSkip?: () => void
}

export function ArticlesOfIncorporationForm({
  companyUuid,
  onNext,
  onBack,
  onSkip,
}: ArticlesOfIncorporationFormProps) {
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  })
  const queryClient = useQueryClient()

  const { data: articlesOfIncorporationDocument, queryKey: aoiQueryKey } =
    useCustomerRepositoryArticlesOfIncorporationRetrieve(companyUuid)
  const companyQueryKey = getCustomerCompanyRetrieveQueryKey(companyUuid)
  const { status: uploadStatus, mutate: uploadArticlesOfIncorporation } =
    useCustomerRepositoryArticlesOfIncorporationUpdate({
      request: {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
      mutation: {
        onError: (error: ErrorType<unknown>) => {
          applyErrorsToForm(
            form.setError,
            error as ErrorType<ErrorResponseType>,
          )
        },
        onSuccess: (data) => {
          queryClient.invalidateQueries({ queryKey: companyQueryKey })
          queryClient.setQueryData(aoiQueryKey, data)
          onNext()
        },
      },
    })

  const currentFileInput = form.watch("file")
  const hasExistingDocument = articlesOfIncorporationDocument?.filename

  function handleSubmit(data: z.infer<typeof FormSchema>) {
    uploadArticlesOfIncorporation({
      data: { ...data, filename: (data.file as unknown as File).name },
      companyUuid,
    })
  }

  function onClickNext(event: React.MouseEvent) {
    if (hasExistingDocument && !currentFileInput) {
      // If the user already has uploaded an Articles of Incorporation document,
      // we don't require them to upload a new one on this page; they can click
      // "Next" to continue.
      event.preventDefault()
      onNext()
    }
    // Otherwise, let the form submit naturally via the button's default
    // type="submit" behavior, which will trigger the onSubmit handler.
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-1 flex-col place-content-between content-between justify-between gap-8"
      >
        <FileUpload
          name={"file"}
          label={"Articles of Incorporation"}
          initialFileName={articlesOfIncorporationDocument?.filename}
          status={uploadStatus}
        />

        {form.formState.errors.root && (
          <p className="text-destructive text-sm font-medium">
            {form.formState.errors.root.message}
          </p>
        )}
        <div className="flex flex-col gap-2 self-stretch sm:self-end">
          <div className="flex flex-col gap-2 sm:flex-row">
            {onBack && (
              <ButtonBack onClick={onBack} className="w-full sm:w-auto" />
            )}
            <ButtonNext
              disabled={!hasExistingDocument && !currentFileInput}
              onClick={onClickNext}
              className="w-full sm:w-auto"
            />
          </div>
          {onSkip && !hasExistingDocument && (
            <Button
              variant="link"
              onClick={onSkip}
              type="button"
              className="self-center sm:self-end"
            >
              Skip for now
            </Button>
          )}
        </div>
      </form>
    </Form>
  )
}
